import { useCallback, useEffect, useState } from 'react';

import type { ExternalTaskFilters } from './useExternalTaskFilters';

const DB_NAME = 'external-task-filters';
const STORE_NAME = 'saved-filters';
const DB_VERSION = 1;

export interface SavedFilter {
  id: string;
  name: string;
  filters: ExternalTaskFilters;
  savedAt: string;
}

const openDatabase = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
};

export const useSavedFilters = () => {
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load all saved filters
  const loadSavedFilters = useCallback(async () => {
    try {
      setIsLoading(true);
      const db = await openDatabase();
      const transaction = db.transaction(STORE_NAME, 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const filters = request.result as SavedFilter[];
        // Sort by savedAt descending (most recent first)
        filters.sort(
          (a, b) =>
            new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime(),
        );
        setSavedFilters(filters);
      };

      request.onerror = () => {
        // eslint-disable-next-line no-console
        console.error('Failed to load saved filters:', request.error);
      };

      db.close();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading saved filters:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Save a new filter
  const saveFilter = useCallback(
    async (name: string, filters: ExternalTaskFilters): Promise<void> => {
      try {
        const db = await openDatabase();
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);

        const savedFilter: SavedFilter = {
          id: `filter-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          name,
          filters,
          savedAt: new Date().toISOString(),
        };

        const request = store.add(savedFilter);

        return await new Promise((resolve, reject) => {
          request.onsuccess = () => {
            loadSavedFilters();
            resolve();
          };

          request.onerror = () => {
            // eslint-disable-next-line no-console
            console.error('Failed to save filter:', request.error);
            reject(request.error);
          };

          transaction.oncomplete = () => {
            db.close();
          };
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error saving filter:', error);
        throw error;
      }
    },
    [loadSavedFilters],
  );

  // Delete a saved filter
  const deleteFilter = useCallback(
    async (id: string): Promise<void> => {
      try {
        const db = await openDatabase();
        const transaction = db.transaction(STORE_NAME, 'readwrite');
        const store = transaction.objectStore(STORE_NAME);

        const request = store.delete(id);

        return await new Promise((resolve, reject) => {
          request.onsuccess = () => {
            loadSavedFilters();
            resolve();
          };

          request.onerror = () => {
            // eslint-disable-next-line no-console
            console.error('Failed to delete filter:', request.error);
            reject(request.error);
          };

          transaction.oncomplete = () => {
            db.close();
          };
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error deleting filter:', error);
        throw error;
      }
    },
    [loadSavedFilters],
  );

  // Load saved filters on mount
  useEffect(() => {
    loadSavedFilters();
  }, [loadSavedFilters]);

  return {
    savedFilters,
    isLoading,
    saveFilter,
    deleteFilter,
    refreshFilters: loadSavedFilters,
  };
};
